import { useCallback, useEffect, useRef, useState } from "react";

import type { HoverSnapshot } from "./types";
import { takeHoverSnapshot } from "./snapshot";

function getMedia(): HoverSnapshot["matchMedia"] {
  if (typeof window === "undefined") {
    return {
      hoverHover: false,
      pointerFine: false,
      anyHover: false,
      anyPointerFine: false,
    };
  }

  return {
    hoverHover: window.matchMedia("(hover: hover)").matches,
    pointerFine: window.matchMedia("(pointer: fine)").matches,
    anyHover: window.matchMedia("(any-hover: hover)").matches,
    anyPointerFine: window.matchMedia("(any-pointer: fine)").matches,
  };
}

export function useHoverDiagnostics(params: { enabled: boolean }) {
  const { enabled } = params;

  const [snapshot, setSnapshot] = useState<HoverSnapshot | null>(null);
  const snapshotRef = useRef<HoverSnapshot | null>(null);

  const [copyState, setCopyState] = useState<"idle" | "success" | "blocked">("idle");
  const [fallbackText, setFallbackText] = useState<string | null>(null);
  const [armed, setArmed] = useState(false);
  const copyTimerRef = useRef<number | null>(null);

  const lastXY = useRef({ x: 0, y: 0 });
  const overlayRectRef = useRef<DOMRect | null>(null);

  // IMPORTANT: attach listeners only when enabled, and always clean up.
  // Previously this used useState() incorrectly and leaked listeners, causing freezes.
  useEffect(() => {
    if (!enabled) return;

    // Cache overlay bounds so pointer tracking doesn't get overwritten when you move
    // into the diagnostics panel to click buttons.
    const updateOverlayRect = () => {
      const el = document.querySelector('[data-hover-diagnostics-root="1"]') as HTMLElement | null;
      overlayRectRef.current = el ? el.getBoundingClientRect() : null;
    };

    updateOverlayRect();
    const rafId = window.requestAnimationFrame(updateOverlayRect);
    window.addEventListener("resize", updateOverlayRect);

    const onMove = (e: PointerEvent | MouseEvent) => {
      const rect = overlayRectRef.current;
      if (
        rect &&
        e.clientX >= rect.left &&
        e.clientX <= rect.right &&
        e.clientY >= rect.top &&
        e.clientY <= rect.bottom
      ) {
        return;
      }

      const target = e.target instanceof Element ? e.target : null;
      // Secondary guard (in case bounds are stale)
      if (target?.closest?.('[data-hover-diagnostics-root="1"]')) return;

      lastXY.current = { x: Math.round(e.clientX), y: Math.round(e.clientY) };
    };

    // Keyboard shortcut: press 'C' to capture without moving mouse
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.repeat) return;

      if (e.code === "KeyC" && !e.metaKey && !e.ctrlKey && !e.altKey) {
        // Only capture if not typing in an input
        const target = e.target as HTMLElement | null;
        const tag = target?.tagName;
        const isEditable =
          tag === "INPUT" ||
          tag === "TEXTAREA" ||
          tag === "SELECT" ||
          (target?.isContentEditable ?? false);

        if (isEditable) return;

        const { x, y } = lastXY.current;
        const next = takeHoverSnapshot({ x, y, media: getMedia() });
        snapshotRef.current = next;
        setSnapshot(next);
      }
    };

    window.addEventListener("pointermove", onMove, { passive: true });
    window.addEventListener("mousemove", onMove, { passive: true });
    // capture=true so we still see the event even if something stops propagation
    document.addEventListener("keydown", onKeyDown, true);

    return () => {
      window.cancelAnimationFrame(rafId);
      window.removeEventListener("resize", updateOverlayRect);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("mousemove", onMove);
      document.removeEventListener("keydown", onKeyDown, true);
    };
  }, [enabled]);

  useEffect(() => {
    return () => {
      if (copyTimerRef.current) window.clearTimeout(copyTimerRef.current);
    };
  }, []);

  // Pick-mode capture: click any element on the page to snapshot it.
  useEffect(() => {
    if (!enabled || !armed) return;

    const onPickPointerDown = (e: PointerEvent) => {
      if (e.button !== 0) return;

      const target = e.target instanceof Element ? e.target : null;
      if (target?.closest?.('[data-hover-diagnostics-root="1"]')) return;

      // Prevent the underlying UI from receiving this click.
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();


      const x = Math.round(e.clientX);
      const y = Math.round(e.clientY);
      lastXY.current = { x, y };

      const next = takeHoverSnapshot({ x, y, media: getMedia() });
      snapshotRef.current = next;
      setSnapshot(next);
      setArmed(false);
    };

    document.addEventListener("pointerdown", onPickPointerDown, true);

    return () => {
      document.removeEventListener("pointerdown", onPickPointerDown, true);
    };
  }, [enabled, armed]);

  // Capture snapshot on demand only
  const captureNow = useCallback(() => {
    if (!enabled) return;
    const { x, y } = lastXY.current;
    const next = takeHoverSnapshot({ x, y, media: getMedia() });
    snapshotRef.current = next;
    setSnapshot(next);
  }, [enabled]);

  const armPick = useCallback(() => {
    if (!enabled) return;
    setArmed((v) => !v);
  }, [enabled]);

  const copySnapshot = useCallback(async () => {
    // Copy the *last captured* snapshot when available, so clicking Copy doesn't
    // re-snapshot the diagnostics panel itself.
    let s = snapshotRef.current;

    if (!s) {
      const { x, y } = lastXY.current;
      s = takeHoverSnapshot({ x, y, media: getMedia() });
      snapshotRef.current = s;
      setSnapshot(s);
    }

    if (!s) return;

    const text = JSON.stringify(s, null, 2);

    try {
      await navigator.clipboard.writeText(text);
      setFallbackText(null);
      setCopyState("success");
      if (copyTimerRef.current) window.clearTimeout(copyTimerRef.current);
      copyTimerRef.current = window.setTimeout(() => setCopyState("idle"), 1200);
    } catch {
      setCopyState("blocked");
      setFallbackText(text);
    }
  }, []);

  return {
    snapshot,
    captureNow,
    armed,
    armPick,
    copySnapshot,
    copyState,
    fallbackText,
  };
}

